<template>
  <q-btn color="primary" class="JobApplicationButton" :class="buttonClass" @click="openDialog">{{ buttonText }}</q-btn>
  <q-dialog class="JobApplicationDialog" v-model="showDialog" persistent>
    <q-card style="max-height: 80vh;">
      <q-card-section class="row bg-primary text-white items-center">
        <h3 class="q-pa-none q-ma-none">Deine Bewerbung</h3>
        <q-btn flat icon="close" @click="closeDialog" class="q-ml-auto" />
      </q-card-section>
      <q-card-section v-if="!success">
        <q-btn v-if="isDebug" color="secondary" @click="fillDebugData">Debug</q-btn>
        <p v-if="jobData">
          Deine Bewerbung für die Stelle:
          <strong>{{ jobData.title.rendered }}</strong>
        </p>
        <p v-else>
          Hier kannst Du Deine Initiativbewerbung einreichen.
        </p>
        <p>
          <q-field label="Anrede *" :error="!!errors.gender" :error-message="errors.gender">
            <q-option-group v-model="form.gender" :options="optionsGender" :rules="rules.gender" inline></q-option-group>
          </q-field>
        </p>
        <p>
          <q-input v-model="form.firstName" :error="!!errors.firstName" :error-message="errors.firstName" :rules="rules.firstName" label="Vorname *" />
        </p>
        <p>
          <q-input v-model="form.lastName" :error="!!errors.lastName" :error-message="errors.lastName" :rules="rules.lastName" label="Nachname *" />
        </p>
        <p>
          <DateField v-model="form.birthdate" :error="!!errors.birthdate" :error-message="errors.birthdate" :rules="rules.birthdate" label="Geburtsdatum *" noPopup />
        </p>
        <h4 class="q-mt-lg q-mb-xs">Deine Kontaktdaten</h4>
        <p class="q-mt-none">
          <q-input v-model="form.email" :error="!!errors.email" :error-message="errors.email" :rules="rules.email" type="email" label="E-Mail *" />
        </p>
        <p>
          <q-input v-model="form.phone" :error="!!errors.phone" :error-message="errors.phone" :rules="rules.phone" label="Telefon" />
        </p>
        <h4 class="q-mt-lg q-mb-none">Thema Führerschein</h4>
        <p class="q-mt-none">
          <q-field label="Welche Fahrzeuge kannst du fahren?" :error="!!errors.vehicleTypes" :error-message="errors.vehicleTypes">
            <q-option-group v-model="form.vehicleTypes" :options="optionsVehicleTypes" inline :rules="rules.vehicleTypes" />
          </q-field>
        </p>
        <h4 class="q-mt-lg q-mb-xs">Erzähl uns von Dir ...</h4>
        <p class="q-mt-none">
          <small>Welche Ausbildung hast Du? Hast Du bereits Berufserfahrung? Hast Du besondere Fähigkeiten?</small>
        </p>
        <q-input v-model="form.misc" :error="!!errors.misc" :error-message="errors.misc" :rules="rules.misc" type="textarea" label="Dein Text" />
        <h4 class="q-mt-xl q-mb-xs">Dateien</h4>
        <p class="q-mt-none">
          Hast Du noch Dateien die Du uns schicken möchtest? Zum Beispiel Deinen Lebenslauf, Zeugnisse oder Zertifikate?
        </p>
        <FileManagementInput v-model="files" @complete="addFiles" :disabled="loading"
          :error="!!errors.files" :error-message="errors.files" :rules="rules.files"
          :allowedMimeTypes="allowedMimeTypes"
          :fileTypesText="fileTypesText"
          :maxFileCount="maxFiles"
          :maxFileSizeSum="maxTotalSize">
        </FileManagementInput>

        <section class="q-mt-md">
          <div v-if="errorMsg" class="errors">{{ errorMsg }}</div>
          <div v-if="loading" class="q-ma-md">
            <loading :loading="loading" />
            Daten werden übermittelt ...
          </div>
          <div class="row items-end actionButtons">
            <q-btn label="Abbrechen" color="grey" @click="closeDialog" />
            <q-space />
            <q-btn label="Absenden" color="primary" size="lg" @click="submitApplication" />
          </div>
        </section>
      </q-card-section>
      <q-card-section v-if="success" class="text-center">
        <h3 class="text-primary">Vielen Dank für Deine Bewerbung!</h3>
        <p>Wir werden uns in Kürze bei Dir melden.</p>
        <div class="row">
          <q-space />
          <q-btn label="Schließen" color="primary" @click="closeDialog" />
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<style scoped>
.errors {
  color: var(--q-negative);
  font-weight: bold;
  background:
  color-mix(in srgb, var(--q-negative) 5%, transparent);
  border: 1px solid var(--q-negative);
  padding: .5em .7em;
  margin: .3rem 0;
  font-style: italic;
}
</style>

<script>
import DateField from '../DateField.vue';
import { submitJobApplication, getJobInfo } from './api';
import { field_required, files_max_size_sum, files_max_file_count, files_only_allowed_mime_types } from './../fieldRules';
import FileManagementInput from './FileManagementInput.vue';
import loading from './../loading.vue';
export default {
  name: 'JobApplication',
  components: { DateField, FileManagementInput, loading },
  props: {
    jobId: { required: true, type: String, default: '-1' },
    buttonText: { required: false, type: String, default: 'Bewerben' },
    buttonClass: { required: false, type: String, default: '' },
  },
  mounted() {
    if(this.isDebug) this.openDialog();
  },
  data: () => ({
    loading: false,
    success: false,
    errors: {},
    errorMsg: null,
    showDialog: false,
    optionsGender: [
      { label: 'Herr', value: 'Herr' },
      { label: 'Frau', value: 'Frau' },
      { label: 'divers / keine Angabe', value: 'Divers / keine Angabe' },
    ],
    optionsVehicleTypes: [
      { label: 'Keine', value: 'Keine' },
      { label: 'PKW', value: 'PKW' },
      { label: 'LKW größer 7,5t', value: 'LKW' },
    ],
    jobData: null,
    form: {
      gender: 'Herr',
      firstName: '',
      lastName: '',
      birthdate: '', // This will be bound to DateField
      vehicleTypes: null,
      email: '',
      phone: '',
      misc: ''
    },
    files: [],
    maxFiles: 10,
    maxTotalSize: 1000 * 1000 * 10, // 10MB
    allowedMimeTypes: [
      'image/jpeg',
      'image/png',
      'application/pdf',
      'application/msword', // doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
      'application/vnd.oasis.opendocument.text', // odt
      'application/x-zip-compressed',
      'application/zip'
    ],
    fileTypesText: 'JPG, PNG, PDF, DOC, DOCX, ODT oder ZIP'
  }),

  computed: {
    isDebug() {
      return import.meta.env.DEV;
    },
    hasErrors() {
      return Object.keys(this.errors).length > 0;
    },
    rules() {
      return {
        gender: [field_required],
        firstName: [field_required],
        lastName: [field_required],
        birthdate: [field_required],
        email: [field_required],
        phone: [],
        vehicleTypes: [field_required],
        files: [
          (value) => value.length === 0 || files_max_size_sum(value, 1000 * 1000 * 10) || 'Die maximale Dateigröße aller Dateien zusammen sollte 10MB nicht überschreiten',
          (value) => value.length === 0 || files_max_file_count(value, 10) || 'Maximal 10 Dateien erlaubt',
          (value) => value.length === 0 || files_only_allowed_mime_types(value, this.allowedMimeTypes, 'Bitte nur Dateien im ' + this.fileTypesText + ' Format auswählen!'),
        ],
        misc: []
      }
    },
  },
  methods: {
    async openDialog() {
      this.showDialog = true;
      try {
        const response = await getJobInfo(this.jobId);
        this.jobData = response.data;
        if(this.jobData.is_job_public === false) {
          this.jobData = null; // If the job is not public, we treat it as an initiative application
        }
      } catch (error) {
        this.jobData = null;
      }
    },
    closeDialog() {
      this.showDialog = false;
      this.reset();
    },
    reset() {
      this.loading = false;
      this.success = false;
      this.errors = {};
      this.errorMsg = null;
      this.form = {
        gender: 'Herr',
        firstName: '',
        lastName: '',
        birthdate: '',
        vehicleTypes: null,
        email: '',
        phone: '',
        misc: ''
      };
      this.files = [];
    },
    addFiles(files) {
      this.files = files;
    },
    async submitApplication() {
      if(this.loading) return;
      this.loading = true;
      this.errors = {};
      this.errorMsg = null;
      try {
        await this.makeRequest(true);//the validation request (without files)
        await this.makeRequest();//the real request
        this.success = true;
      } catch(error) {
        this.errors = error.response?.data?.errors ?? {};
        this.errorMsg = error.response?.data?.error ?? 'Ein Fehler ist aufgetreten.';
        this.loading = false;
        console.error('Error during validation:', error);
      }
      this.loading = false;
    },

    async makeRequest(isPrecheck = false) {
      let requestData = new FormData();
      requestData.append('isPrecheck', isPrecheck);
      requestData.append('form', JSON.stringify(Object.assign({ jobId: parseInt(this.jobId) }, this.form)));
      //add files to the form
      if (this.files.length > 0) {
        for (const file of this.files) {
          if(isPrecheck) {
            let fileData = {
              name: file.name,
              type: file.type,
              size: file.size,
            };
            requestData.append('files[]', JSON.stringify(fileData)); //just the name for precheck
          } else {
            requestData.append('files[]', file);
          }
        }
      }

      /*
      if(this.files.length>0) {
        requestData.form.files = [];
        for(const file of this.files) {
          let fileData = {
            name: file.name,
            mimeType: file.type,
            size: file.size,
          };
          if(!isPrecheck) fileData.content = window.btoa( await this.readFileContent(file) );//the content should only be applied if its not the precheck
          requestData.form.files.push(fileData);
        }
      }
        */
      return submitJobApplication(requestData);
    },
    readFileContent(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        reader.onload = () => {
          // Convert ArrayBuffer to binary string
          const arrayBuffer = reader.result;
          const uint8Array = new Uint8Array(arrayBuffer);
          let binaryString = '';
          for (let i = 0; i < uint8Array.length; i++) {
            binaryString += String.fromCharCode(uint8Array[i]);
          }
          resolve(binaryString);
        };
        reader.onerror = () => {
          reject(reader.error);
        };
      });
    },
    fillDebugData() {
      this.form = {
        gender: 'Herr',
        firstName: 'Max',
        lastName: 'Mustermann',
        birthdate: '1990-01-01',
        vehicleTypes: 'PKW',
        email: 'max.mustermann@example.com',
        phone: '+491234567890',
        misc: 'Keine weiteren Informationen'
      }
    },
  },
};
</script>